import React from 'react';
import { Check, X } from 'lucide-react';
import KaTeXRenderer from '../common/KaTeXRenderer';
import { QuestionAnalysis } from '../../services/quiz/QuizScoringService';
import { BankQuestion } from '../../services/bankQuestionService_moi';

interface TLUSolutionDisplayProps {
    question: BankQuestion;
    userAnswer: any;
    analysis: QuestionAnalysis;
}

export const TLUSolutionDisplay: React.FC<TLUSolutionDisplayProps> = ({
    question,
    userAnswer,
    analysis
}) => {
    // userAnswer structure: { userAnswer: string, aiGrade: GradeResult }

    const aiGrade = userAnswer?.aiGrade;
    const imageUrl = aiGrade?.imageUrl;
    const parts = aiGrade?.parts || [];
    const overallFeedback = aiGrade?.overallFeedback;

    if (!aiGrade) {
        return (
            <div className="text-red-500 italic p-4 bg-red-50 rounded-lg">
                Không tìm thấy kết quả chấm AI. Có thể bạn chưa nộp bài hoặc có lỗi xảy ra.
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Image Display */}
            {imageUrl && (
                <div className="rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                    <img
                        src={imageUrl}
                        alt="Bài làm của học sinh"
                        className="w-full h-auto max-h-[400px] object-contain mx-auto"
                    />
                </div>
            )}

            {/* AI Feedback */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                <h4 className="font-semibold text-blue-800 mb-2">Nhận xét của AI:</h4>
                <p className="text-blue-900 text-sm whitespace-pre-line">{overallFeedback}</p>
            </div>

            {/* Detailed Parts */}
            <div className="space-y-2">
                <h4 className="font-medium text-gray-900 text-sm">Chi tiết chấm điểm:</h4>
                {parts.map((part: any, idx: number) => (
                    <div key={idx} className="text-sm p-3 bg-white rounded-lg border border-gray-200">
                        <div className="flex justify-between items-start mb-1">
                            <div className="flex items-center gap-1.5">
                                {part.isCorrect ? (
                                    <Check className="w-4 h-4 text-green-500" />
                                ) : (
                                    <X className="w-4 h-4 text-red-500" />
                                )}
                                <span className={part.isCorrect ? 'text-green-700 font-medium' : 'text-red-700 font-medium'}>
                                    {part.score}/{part.maxScore}đ
                                </span>
                            </div>
                        </div>
                        <div className="mb-1">
                            <KaTeXRenderer content={part.content} />
                        </div>
                        <div className="text-xs text-gray-500 bg-gray-50 p-1.5 rounded">
                            {part.feedback}
                        </div>
                    </div>
                ))}
            </div>

            {/* Teacher Solution (if available) */}
            {question.loi_giai && (
                <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-100">
                    <h4 className="font-semibold text-green-800 mb-2">Lời giải tham khảo:</h4>
                    <KaTeXRenderer content={question.loi_giai} />
                </div>
            )}
        </div>
    );
};
